import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="attendance-members"
export default class extends Controller {
  static targets = ["store", "query"];
  static values = { formMembersUrl: String };

  connect() {
    this.selections = new Map();
    this.readStore();
    this.applyState();
  }

  get checkboxes() {
    return Array.from(
      this.element.querySelectorAll(
        "[data-attendance-members-target='checkbox']",
      ),
    );
  }

  get counts() {
    return Array.from(
      this.element.querySelectorAll("[data-attendance-members-target='count']"),
    );
  }

  get checkboxAll() {
    return this.element.querySelector(
      "[data-attendance-members-target='checkboxAll']",
    );
  }

  toggle(event) {
    const cb = event.target;
    const id = cb.dataset.memberId;
    if (cb.checked) {
      const { adult, child } = this.rowCounts(id);
      this.selections.set(id, {
        sector_id: cb.dataset.sectorId || "",
        adult_guest_count: adult,
        child_guest_count: child,
      });
    } else {
      this.selections.delete(id);
    }
    this.writeStore();
    this.syncCheckboxAll();
  }

  updateCount(event) {
    const input = event.target;
    if (Number(input.value) < 0) input.value = "0";
    const entry = this.selections.get(input.dataset.memberId);
    if (!entry) return;
    entry[input.dataset.field] = input.value || "0";
    this.writeStore();
  }

  toggleAll(event) {
    const checked = event.target.checked;
    this.checkboxes.forEach((cb) => {
      if (cb.checked !== checked) {
        cb.checked = checked;
        cb.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  search(event) {
    if (event) event.preventDefault();
    const frame = this.element.querySelector(
      "turbo-frame#attendance_member_list",
    );
    if (!frame) return;
    const url = new URL(this.formMembersUrlValue, window.location.origin);
    const query = this.hasQueryTarget ? this.queryTarget.value.trim() : "";
    if (query) url.searchParams.set("q", query);
    frame.src = url.pathname + url.search;
  }

  clear() {
    if (this.hasQueryTarget) this.queryTarget.value = "";
    this.search();
  }

  applyState() {
    this.checkboxes.forEach((cb) => {
      cb.checked = this.selections.has(cb.dataset.memberId);
    });
    this.counts.forEach((input) => {
      const entry = this.selections.get(input.dataset.memberId);
      if (entry) input.value = entry[input.dataset.field] ?? "0";
    });
    this.syncCheckboxAll();
  }

  syncCheckboxAll() {
    const all = this.checkboxAll;
    if (!all) return;
    const boxes = this.checkboxes;
    const checked = boxes.filter((cb) => cb.checked).length;
    all.checked = boxes.length > 0 && checked === boxes.length;
    all.indeterminate = checked > 0 && checked < boxes.length;
  }

  rowCounts(id) {
    const value = (field) =>
      this.counts.find(
        (i) => i.dataset.memberId === id && i.dataset.field === field,
      )?.value || "0";
    return {
      adult: value("adult_guest_count"),
      child: value("child_guest_count"),
    };
  }

  readStore() {
    if (!this.hasStoreTarget) return;
    this.storeTarget
      .querySelectorAll("input[name$='[member_id]']")
      .forEach((input) => {
        const id = input.value;
        this.selections.set(id, {
          sector_id: this.storedValue(id, "sector_id"),
          adult_guest_count: this.storedValue(id, "adult_guest_count"),
          child_guest_count: this.storedValue(id, "child_guest_count"),
        });
      });
  }

  storedValue(id, field) {
    const input = this.storeTarget.querySelector(
      `input[name='members[${id}][${field}]']`,
    );
    return input ? input.value : "0";
  }

  writeStore() {
    if (!this.hasStoreTarget) return;
    const html = [];
    this.selections.forEach((data, id) => {
      html.push(this.hidden(id, "member_id", id));
      html.push(this.hidden(id, "sector_id", data.sector_id));
      html.push(this.hidden(id, "adult_guest_count", data.adult_guest_count));
      html.push(this.hidden(id, "child_guest_count", data.child_guest_count));
    });
    this.storeTarget.innerHTML = html.join("");
  }

  hidden(id, field, value) {
    const safe = String(value == null ? "" : value).replace(/"/g, "&quot;");
    return `<input type="hidden" name="members[${id}][${field}]" value="${safe}">`;
  }
}
